import type { Hint } from '@flobi/asset-library/hints';
import { keyParts, keysFor } from '../../ui/keys';
import {
  AltDragScene,
  BrandColourScene,
  BrandTypeScene,
  BuilderScene,
  ByColourScene,
  ClonerScene,
  CmykScene,
  DistributeScene,
  EffectorsScene,
  ExportAllScene,
  ExportVideoScene,
  EyedropperScene,
  GroupScene,
  MaskScene,
  MyStuffScene,
  SameFillScene,
  TimingScene,
  ToolKeysScene,
  ZoomKeysScene,
} from './artworkHintScenes';

/**
 * Artwork's hints: the corner card's catalog, read by `@flobi/asset-library/hints`. Each one
 * says what it shows (the one control it is about, drawn with the editor's own classes and
 * icons in artworkHintScenes.tsx), what makes it due (a signal the editor reports with `emitHintSignal`), and what its
 * button does (`action.id`, handled in ArtworkHintsHost.tsx).
 *
 * Signals Artwork reports (numbers and flags only reach the server):
 *   layer.duplicated { viaButton }          the quick bar's / canvas menu's Duplicate; ⌘D with something chosen
 *   layer.altCopied                         ⌥-drag made copies (tools/select.ts)
 *   layers.grouped   { viaButton }          the quick bar's Group, the canvas menu's Group selection
 *   layers.arranged  { viaMenu }            Arrange from the quick bar or the canvas menu
 *   layers.aligned   { count, spread }      Align from the quick bar or the canvas menu; layers.distributed for Distribute
 *   layers.pickedInside { nested }          a layer inside a group picked in Layers
 *   selection.added  { size }               a shift-click added a layer; selection.same: Select Same Fill/Size
 *   shapes.combined  { intersect }          the quick bar's Combine menu
 *   builder.merged   { pieces }             the Shape Builder's Merge, pieces picked by hand
 *   builder.byColour                        the Shape Builder's Merge by Colour
 *   builder.used / cloner.used              the tool picked up, however it was picked
 *   effector.added / slice.made             an effector added (tools/repeat.ts), a slice drawn
 *   view.zoomed      { viaMenu }            Zoom to fit / Actual size from the zoom menu or the canvas menu
 *   tool.fromMenu { keyed } / tool.select { viaButton } / library.opened { viaButton }  the dock
 *   layer.nudged { run }                    a run of one-unit arrow nudges, told when it ends
 *   image.placed { fromDisk } / share.opened { viaButton }  Place image's file dialog; Share from a button
 *   shortcut.enterGroup / selection.masked  Return into a group; a mask made
 *   export.opened                           the Export panel opened
 *   export.done { all, layers, fileBoards, pdf, cmyk }  an export made (ExportPanel); with
 *     export.all, export.selection, export.video, document.cmyk as fitting
 *   animate.recipe / animate.layerEdited / animate.played { viaButton } / animate.timingOpened  the Animate bar
 *   colour.typed / colour.eyedropped / colour.offBrand / colour.brandPicked  the colour picker
 *   font.picked { brand, hasBrand } / brand.placed / mystuff.placed  fonts, the Brand tab, My Stuff
 *   shortcut.*                              the keys themselves (shortcutSignals.ts)
 *
 * The `when` rules are plain data in flobi-hints-back's format (catalogs/artwork.catalog.ts there),
 * which decides when the server is reachable; these decide when it isn't. Keep the two in step.
 */

/** A shortcut's keys as this keyboard says them: ⇧ ⌘ E on a Mac, Ctrl Shift E elsewhere. */
const caps = (shortcut: string) => keyParts(shortcut) ?? [...shortcut];
const EXPORT_KEYS = caps('⇧⌘E');
const GOT_IT = { id: 'got-it', label: 'Got it' } as const;

export const ARTWORK_HINTS: Hint[] = [
  {
    id: 'artwork.cloner',
    app: 'artwork',
    title: 'Make many copies at once',
    body: "You've been duplicating one by one. The Cloner repeats a selection in a row, grid or circle, each copy editable.",
    media: { type: 'node', render: () => <ClonerScene /> },
    action: { id: 'tool-cloner', label: 'Use the Cloner' },
    learn: 'learn:artwork/cloner',
    priority: 3,
    when: { signal: 'layer.duplicated', count: 6, withinDays: 7 },
    learnedBy: ['cloner.used'],
  },
  {
    id: 'artwork.shape-builder',
    app: 'artwork',
    title: 'Build shapes from overlaps',
    body: 'Select overlapping shapes, drag across the pieces you want, then press Merge or Delete.',
    media: { type: 'node', render: () => <BuilderScene /> },
    action: { id: 'tool-builder', label: 'Use Shape Builder' },
    learn: 'learn:artwork/shape-builder',
    priority: 2,
    when: { signal: 'shapes.combined', count: 3, withinDays: 7 },
    learnedBy: ['builder.used'],
  },
  {
    id: 'artwork.merge-by-colour',
    app: 'artwork',
    title: 'Combine same-colour shapes at once',
    body: 'In Shape Builder, Merge by Colour joins every touching shape that shares a fill.',
    media: { type: 'node', render: () => <ByColourScene /> },
    action: { id: 'got-it', label: 'Got it' },
    learn: 'learn:artwork/shape-builder',
    priority: 2,
    when: { signal: 'builder.merged', count: 4, withinDays: 7 },
    learnedBy: ['builder.byColour'],
  },
  {
    id: 'artwork.group-shortcut',
    app: 'artwork',
    title: `Group with ${keysFor('⌘G')}`,
    body: `Select two or more layers first. ${keysFor('⇧⌘G')} ungroups them again.`,
    media: { type: 'node', render: () => <GroupScene /> },
    action: { id: 'got-it', label: 'Got it' },
    learn: 'learn:artwork/group',
    priority: 1,
    when: { signal: 'layers.grouped', where: { viaButton: true }, count: 3, withinDays: 7 },
    learnedBy: ['shortcut.group'],
  },
  {
    id: 'artwork.export-shortcut',
    app: 'artwork',
    title: `Open Export with ${keysFor('⇧⌘E')}`,
    body: 'Works from anywhere in the editor, without reaching for the toolbar.',
    media: { type: 'keys', keys: EXPORT_KEYS, result: 'Export' },
    // Not Open Export: pressing a card's button counts as learning it, and a press of the
    // button is not a press of the key (review 9 Oct).
    action: { id: 'got-it', label: 'Got it' },
    learn: 'learn:artwork/export',
    priority: 1,
    when: { signal: 'export.opened', count: 3, withinDays: 7 },
    learnedBy: ['shortcut.export'],
  },
  // ── Approved 9 Oct (gallery): the rest of the catalog.
  {
    id: 'artwork.duplicate-shortcut',
    app: 'artwork',
    title: `Duplicate with ${keysFor('⌘D')}`,
    body: 'Copies the selection and offsets it slightly, ready to drag.',
    media: { type: 'keys', keys: caps('⌘D'), result: 'Duplicate' },
    action: GOT_IT,
    learn: 'learn:artwork/duplicate',
    priority: 1,
    when: { signal: 'layer.duplicated', where: { viaButton: true }, count: 3, withinDays: 7 },
    learnedBy: ['shortcut.duplicate'],
  },
  {
    id: 'artwork.alt-drag-copy',
    app: 'artwork',
    title: `Drag a copy with ${keysFor('⌥')}`,
    body: `Hold ${keysFor('⌥')} as you move a layer. The original stays put and the duplicate lands where you let go.`,
    media: { type: 'node', pad: false, render: () => <AltDragScene /> },
    action: GOT_IT,
    learn: 'learn:artwork/duplicate',
    priority: 2,
    when: { signal: 'layer.duplicated', where: { viaButton: false }, count: 4, withinDays: 7 },
    learnedBy: ['layer.altCopied'],
  },
  {
    id: 'artwork.effectors',
    app: 'artwork',
    title: 'Vary cloned shapes in one go',
    body: 'Add an effector to a Cloner to scatter, grow, twist, fade or magnify the copies. Each one stays editable.',
    media: { type: 'node', pad: false, render: () => <EffectorsScene /> },
    action: GOT_IT,
    learn: 'learn:artwork/effectors',
    priority: 2,
    when: { signal: 'cloner.used', count: 3, withinDays: 14 },
    learnedBy: ['effector.added'],
  },
  {
    id: 'artwork.tool-keys',
    app: 'artwork',
    title: 'Pick tools with one key',
    body: 'P Pen, M Rectangle, L Ellipse, R Transform, E Cut. Menus show each letter beside the tool name.',
    media: { type: 'node', pad: false, render: () => <ToolKeysScene /> },
    action: GOT_IT,
    learn: 'learn:artwork/tool-keys',
    priority: 2,
    when: { signal: 'tool.fromMenu', where: { keyed: true }, count: 8, withinDays: 7 },
    learnedBy: ['shortcut.tool'],
  },
  {
    id: 'artwork.select-key',
    app: 'artwork',
    title: 'Press V to go back to Select',
    body: 'Use it after drawing, instead of reaching for the arrow in the dock.',
    media: { type: 'keys', keys: ['V'], result: 'Select' },
    action: GOT_IT,
    learn: 'learn:artwork/tool-keys',
    priority: 1,
    when: { signal: 'tool.select', where: { viaButton: true }, count: 6, withinDays: 3 },
    learnedBy: ['shortcut.select'],
  },
  {
    id: 'artwork.arrange-shortcuts',
    app: 'artwork',
    title: `Reorder layers with ${keysFor('⌘]')} and ${keysFor('⌘[')}`,
    body: `${keysFor('⌘]')} brings a layer forward, ${keysFor('⌘[')} sends it backward. Add ${keysFor('⇧')} to go straight to the front or back.`,
    media: { type: 'keys', keys: caps('⌘]'), result: 'Bring Forward' },
    action: GOT_IT,
    learn: 'learn:artwork/arrange',
    priority: 2,
    when: { signal: 'layers.arranged', where: { viaMenu: true }, count: 4, withinDays: 7 },
    learnedBy: ['shortcut.arrange'],
  },
  {
    id: 'artwork.enter-group',
    app: 'artwork',
    title: 'Step into a group with Return',
    body: `With a group selected, this picks the layers inside. ${keysFor('⇧')}Return selects the parent group again.`,
    media: { type: 'keys', keys: caps('↩'), result: 'Select inside' },
    action: GOT_IT,
    learn: 'learn:artwork/groups',
    priority: 3,
    when: { signal: 'layers.pickedInside', where: { nested: true }, count: 5, withinDays: 7 },
    learnedBy: ['shortcut.enterGroup'],
  },
  {
    id: 'artwork.zoom-keys',
    app: 'artwork',
    title: 'Zoom with 0 and 1',
    body: '0 fits everything on screen and 1 shows Actual Size, without opening the zoom menu.',
    media: { type: 'node', pad: false, render: () => <ZoomKeysScene /> },
    action: GOT_IT,
    learn: 'learn:artwork/navigate',
    priority: 3,
    when: { signal: 'view.zoomed', where: { viaMenu: true }, count: 4, withinDays: 7 },
    learnedBy: ['shortcut.zoom'],
  },
  {
    id: 'artwork.big-nudge',
    app: 'artwork',
    title: 'Nudge in bigger steps',
    body: `Hold ${keysFor('⇧')} with the arrow keys to move a layer 10 units per press instead of 1.`,
    media: { type: 'keys', keys: [...caps('⇧'), '→'], result: '10 units' },
    action: GOT_IT,
    learn: 'learn:artwork/navigate',
    priority: 3,
    when: { signal: 'layer.nudged', where: { run: { gte: 15 } }, count: 3, withinDays: 7 },
    learnedBy: ['shortcut.bigNudge'],
  },
  {
    id: 'artwork.distribute',
    app: 'artwork',
    title: 'Space layers evenly',
    body: 'Select three or more, then Align › Distribute Horizontally or Distribute Vertically.',
    media: { type: 'node', pad: false, render: () => <DistributeScene /> },
    action: GOT_IT,
    learn: 'learn:artwork/align',
    priority: 2,
    when: { signal: 'layers.aligned', where: { count: { gte: 3 }, spread: false }, count: 3, withinDays: 7 },
    learnedBy: ['layers.distributed'],
  },
  {
    id: 'artwork.select-same-fill',
    app: 'artwork',
    title: 'Pick out every shape in one colour',
    body: 'With the Select tool, choose one shape, then Edit › Select Same Fill.',
    media: { type: 'node', pad: false, render: () => <SameFillScene /> },
    action: GOT_IT,
    learn: 'learn:artwork/select',
    priority: 2,
    when: { signal: 'selection.added', where: { size: { gte: 5 } }, count: 3, withinDays: 7 },
    learnedBy: ['selection.same'],
  },
  {
    id: 'artwork.mask-instead',
    app: 'artwork',
    title: 'Crop without losing shapes',
    body: 'Intersect cuts for good. Mask in Properties clips the rest to the backmost shape, and each stays movable.',
    media: { type: 'node', pad: false, render: () => <MaskScene /> },
    action: { id: 'open-properties', label: 'Open Properties' },
    learn: 'learn:artwork/mask',
    priority: 3,
    when: { signal: 'shapes.combined', where: { intersect: true }, count: 2, withinDays: 14 },
    learnedBy: ['selection.masked'],
  },
  {
    id: 'artwork.slices',
    app: 'artwork',
    title: 'Turn parts of the page into files',
    body: 'Press K and drag a box. In Export, SVG slices saves each slice separately.',
    media: { type: 'keys', keys: ['K'], result: 'Slice' },
    action: { id: 'tool-slice', label: 'Use Slice' },
    learn: 'learn:artwork/slices',
    priority: 3,
    when: { signal: 'export.selection', count: 3, withinDays: 7 },
    learnedBy: ['slice.made'],
  },
  {
    id: 'artwork.export-all',
    app: 'artwork',
    title: 'Export every artboard at once',
    body: 'In Export, choose All under What. You get a ZIP, or a single PDF.',
    media: { type: 'node', pad: false, render: () => <ExportAllScene /> },
    action: { id: 'open-export', label: 'Open Export' },
    learn: 'learn:artwork/export',
    priority: 2,
    when: { signal: 'export.done', where: { all: false, layers: false, fileBoards: { gte: 2 } }, count: 3, withinDays: 1 },
    learnedBy: ['export.all'],
  },
  {
    id: 'artwork.export-video',
    app: 'artwork',
    title: 'Export your animation as video',
    body: 'In Export, pick MP4 or GIF. Animation exports one artboard at a time.',
    media: { type: 'node', pad: false, render: () => <ExportVideoScene /> },
    action: { id: 'open-export', label: 'Open Export' },
    learn: 'learn:artwork/animate-export',
    priority: 1,
    when: { signal: 'animate.recipe', count: 2, withinDays: 14 },
    learnedBy: ['export.video'],
  },
  {
    id: 'artwork.animate-space',
    app: 'artwork',
    title: 'Play and pause with Space',
    body: 'Works while Animate is open. Holding Space still pans the page.',
    media: { type: 'keys', keys: ['Space'], result: 'Play / Pause' },
    action: GOT_IT,
    learn: 'learn:artwork/animate',
    priority: 2,
    when: { signal: 'animate.played', where: { viaButton: true }, count: 4, withinDays: 7 },
    learnedBy: ['shortcut.animatePlay'],
  },
  {
    id: 'artwork.animate-timing',
    app: 'artwork',
    title: 'Set exactly when each layer moves',
    body: "Press Timing on the Animate bar, then drag a layer's bar to change when it arrives and how long it stays.",
    media: { type: 'node', pad: false, render: () => <TimingScene /> },
    action: GOT_IT,
    learn: 'learn:artwork/animate',
    priority: 3,
    when: { signal: 'animate.layerEdited', count: 6, withinDays: 7 },
    learnedBy: ['animate.timingOpened'],
  },
  {
    id: 'artwork.cmyk-for-print',
    app: 'artwork',
    title: 'Set the file up for print',
    body: 'Switch to CMYK in Document settings. Colours then show as they will print, and swatches turn into inks.',
    media: { type: 'node', pad: false, render: () => <CmykScene /> },
    action: { id: 'document-settings', label: 'Open settings' },
    learn: 'learn:artwork/cmyk',
    priority: 2,
    when: { signal: 'export.done', where: { pdf: true, cmyk: false }, count: 2, withinDays: 30 },
    learnedBy: ['document.cmyk'],
  },
  {
    id: 'artwork.eyedropper',
    app: 'artwork',
    title: 'Take a colour from the page',
    body: 'Press the eyedropper beside the colour square, then click anywhere on screen to sample it.',
    media: { type: 'node', pad: false, render: () => <EyedropperScene /> },
    action: GOT_IT,
    learn: 'learn:artwork/colour',
    priority: 2,
    when: { signal: 'colour.typed', count: 5, withinDays: 7 },
    learnedBy: ['colour.eyedropped'],
  },
  {
    id: 'artwork.brand-colour',
    app: 'artwork',
    title: "Snap to your brand's colour",
    body: 'When the picker says Off-brand, it names the nearest match. Press Use to switch.',
    media: { type: 'node', pad: false, render: () => <BrandColourScene /> },
    action: GOT_IT,
    learn: 'learn:artwork/brand',
    priority: 1,
    when: { signal: 'colour.offBrand', count: 3, withinDays: 7 },
    learnedBy: ['colour.brandPicked'],
  },
  {
    id: 'artwork.brand-type',
    app: 'artwork',
    title: "Use your brand's type",
    body: 'The Brand tab in Assets has Heading and Body text set in the right fonts. Press one to place it.',
    media: { type: 'node', pad: false, render: () => <BrandTypeScene /> },
    action: { id: 'open-brand', label: 'Open Brand' },
    learn: 'learn:artwork/brand',
    priority: 1,
    when: { signal: 'font.picked', where: { brand: false, hasBrand: true }, count: 3, withinDays: 7 },
    learnedBy: ['brand.placed'],
  },
  {
    id: 'artwork.my-stuff',
    app: 'artwork',
    title: 'Place pictures from Drive',
    body: 'My Stuff in Assets has your images, clips and SVGs. Drag one onto the page.',
    media: { type: 'node', pad: false, render: () => <MyStuffScene /> },
    action: { id: 'open-my-stuff', label: 'Open My Stuff' },
    learn: 'learn:artwork/my-stuff',
    priority: 2,
    when: { signal: 'image.placed', where: { fromDisk: true }, count: 3, withinDays: 14 },
    learnedBy: ['mystuff.placed'],
  },
  {
    id: 'artwork.assets-shortcut',
    app: 'artwork',
    title: `Open Assets with ${keysFor('⌃⌘S')}`,
    body: 'The same keys hide it again, from anywhere in the editor.',
    media: { type: 'keys', keys: caps('⌃⌘S'), result: 'Assets' },
    action: GOT_IT,
    learn: 'learn:artwork/assets',
    priority: 3,
    when: { signal: 'library.opened', where: { viaButton: true }, count: 6, withinDays: 7 },
    learnedBy: ['shortcut.library'],
  },
  {
    id: 'artwork.share-shortcut',
    app: 'artwork',
    title: `Share with ${keysFor('⇧⌘S')}`,
    body: 'Opens Share, where you invite people or copy the view link.',
    media: { type: 'keys', keys: caps('⇧⌘S'), result: 'Share' },
    action: GOT_IT,
    learn: 'learn:artwork/share',
    priority: 3,
    when: { signal: 'share.opened', where: { viaButton: true }, count: 3, withinDays: 14 },
    learnedBy: ['shortcut.share'],
  },
];
